import {AddressElement, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js"
import {useMutation} from "@tanstack/react-query"
import {BaseButton, BasePopup} from "components"
import {handleError, toastSuccess} from "helpers"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import styles from "./PaymentMethods.module.css"

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    profileId: number
}

const PaymentMethodPopup = ({isShow, onClose, onSuccess, profileId}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [loadKey, setLoadKey] = useState(Math.random())

    useEffect(() => {
        if (!isShow) {
            setLoadKey(Math.random())
        }
    }, [isShow])

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="CREDIT_CARD" width="60vw">
            <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                <h1 className={styles.title}>{t("title.addPaymentMethod")}</h1>
                <PaymentMethodForm profileId={profileId} onSuccess={onSuccess} />
            </StripeElementsProvider>
        </BasePopup>
    )
}

export default PaymentMethodPopup

const PaymentMethodForm = ({profileId, onSuccess}: {profileId: number; onSuccess: () => void}) => {
    const {t} = useTranslation(["payment", "common"])
    const stripe = useStripe()
    const elements = useElements()

    const {isLoading, mutate: addPaymentMethod} = useMutation({
        mutationFn: async (event: React.FormEvent) => {
            event.preventDefault()

            if (!stripe || !elements || !profileId) {
                return
            }
            const {error, setupIntent} = await stripe.confirmSetup({
                elements,
                redirect: "if_required"
            })
            if (error) {
                throw error
            }
            return studentPaymentService.stripeAttachPaymentMethod(profileId, setupIntent.payment_method as string)
        },
        onError: (error) => handleError(error),
        onSuccess: () => {
            toastSuccess(t("msg.paymentMethod.addedSuccessfully"))
            onSuccess()
        }
    })

    return (
        <form onSubmit={addPaymentMethod}>
            <AddressElement options={{mode: "billing"}} />
            <PaymentElement className="mt-24" />
            <BaseButton
                size="small"
                title={t("common:action.add")}
                disabled={!stripe || !elements}
                htmlType="submit"
                loading={isLoading}
                className="w-full mt-24"
            />
        </form>
    )
}
